/**
 * LocationsModal — Administrar ubicaciones de una bodega (CRUD inline).
 */
import { useState, useEffect } from 'react'
import { Plus, Trash2, MapPin, Loader2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { useWarehouseLocations, type StorageLocation } from '../hooks/useInventory'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { ToastContainer, useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import { TextField } from '@/components/ds/TextField'
import { Modal } from '@/components/ds/Modal'

interface Props {
  warehouseId: string
  warehouseName: string
  canEdit?: boolean
  onClose: () => void
}

export default function LocationsModal({ warehouseId, warehouseName, canEdit = false, onClose }: Props) {
  const toast = useToast()
  const qc = useQueryClient()
  const { data, isLoading } = useWarehouseLocations(warehouseId)
  const locations = data?.locations || []

  // Form state for new location
  const [newCode, setNewCode] = useState('')
  const [newName, setNewName] = useState('')
  const [newZone, setNewZone] = useState('')

  const createMutation = useMutation({
    mutationFn: async (d: any) => {
      const res = await apiClient.post('/inventory/locations/', d)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouse-locations', warehouseId] })
      toast.success('Ubicación creada')
      setNewCode('')
      setNewName('')
      setNewZone('')
    },
    onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al crear ubicación')),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.patch(`/inventory/locations/${id}/`, { is_active: false })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouse-locations', warehouseId] })
      toast.success('Ubicación desactivada')
    },
    onError: (e: any) => toast.error(extractErrorMessage(e, 'Error')),
  })

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [onClose])

  const handleCreate = () => {
    if (!newCode.trim() || !newName.trim()) {
      toast.error('Código y nombre son obligatorios')
      return
    }
    createMutation.mutate({
      warehouse: warehouseId,
      code: newCode.trim(),
      name: newName.trim(),
      zone: newZone.trim(),
    })
  }

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><MapPin size={18} className="text-primary" /> Ubicaciones</span>} subtitle={warehouseName} size="2xl">
      {/* Body */}
      <div className="flex-1 overflow-y-auto space-y-4">
        {/* Add new — only for users with edit permission */}
        {canEdit && (
          <div className="p-4 bg-glass-3 border border-divider rounded-xl space-y-3">
            <p className="text-xs font-bold text-content-muted uppercase tracking-wider">Nueva ubicación</p>
            <div className="grid grid-cols-3 gap-3">
              <TextField
                dense
                label="Código"
                required
                value={newCode}
                onChange={e => setNewCode(e.target.value)}
                placeholder="A-03-2"
              />
              <TextField
                dense
                label="Nombre"
                required
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Pasillo A, Estante 3, Nivel 2"
              />
              <TextField
                dense
                label="Zona"
                value={newZone}
                onChange={e => setNewZone(e.target.value)}
                placeholder="Almacenaje"
              />
            </div>
            <button onClick={handleCreate} disabled={createMutation.isPending}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
              {createMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Agregar
            </button>
          </div>
        )}

        {/* Existing locations */}
        <DataTable
          columns={[
            { key: 'code', header: 'Código', render: (l: StorageLocation) => <span className="font-mono font-medium text-content">{l.code}</span> },
            { key: 'name', header: 'Nombre' },
            { key: 'zone', header: 'Zona', render: (l: StorageLocation) => l.zone || '—' },
            {
              key: 'acciones', header: 'Acc.', width: 64,
              render: (l: StorageLocation) => canEdit ? (
                <div className="text-center">
                  <button onClick={() => deleteMutation.mutate(l.id)}
                    disabled={deleteMutation.isPending}
                    title="Desactivar ubicación"
                    className="p-1 rounded-lg hover:bg-danger-surface text-content-subtle hover:text-danger-solid disabled:opacity-40">
                    <Trash2 size={14} />
                  </button>
                </div>
              ) : null,
            },
          ]}
          rows={locations}
          rowKey={(l: StorageLocation) => l.id}
          loading={isLoading}
          empty={<EmptyState icon={Trash2} size="sm" title="Sin ubicaciones." hint="Agrega la primera arriba." />}
          // Ya vive dentro del modal: un panel propio se ve como una caja
          // dentro de otra.
          surface={false}
          minWidth={480}
          caption="Ubicaciones de la bodega"
        />
      </div>

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </Modal>
  )
}
